import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * Container de lista ordenável genérico, sem dependência de domínio.
 *
 * Props:
 * - itens: array de itens a ordenar.
 * - extrairId: função que devolve o id estável de um item (string ou número).
 * - onReordenar: recebe o array JÁ reordenado ao soltar. Não recebe índices.
 * - children: função render-prop (item, id, indice) => ReactNode, ou nós prontos.
 *   O `indice` é a posição 0-based já computada no map, para o consumidor numerar
 *   itens sem recalcular indexOf por item (O(n²) na lista inteira).
 *
 * Acessibilidade: PointerSensor para mouse/toque e KeyboardSensor com
 * sortableKeyboardCoordinates para navegação por teclado (foco → levantar →
 * setas → soltar), com região aria-live anunciando a nova posição.
 */
export function DommusSortable({ itens, extrairId, onReordenar, children, "aria-label": ariaLabel }) {
  const [anuncio, setAnuncio] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const ids = itens.map((item) => extrairId(item));

  function handleDragEnd(evento) {
    const { active, over } = evento;
    if (!over || active.id === over.id) {
      return;
    }

    const indiceAntigo = ids.indexOf(active.id);
    const indiceNovo = ids.indexOf(over.id);
    if (indiceAntigo === -1 || indiceNovo === -1) {
      return;
    }

    const reordenados = arrayMove(itens, indiceAntigo, indiceNovo);
    setAnuncio(`Item movido para a posição ${indiceNovo + 1} de ${itens.length}.`);
    onReordenar(reordenados);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      // Mede os droppables continuamente. Sem isso, a primeira seta de um arraste por
      // teclado pode chegar antes de o dnd-kit medir os droppables (que, no default
      // WhileDragging, só são medidos ao iniciar o arraste), e o movimento vira um
      // no-op sobre a própria posição — arraste por teclado "travado". `Always`
      // garante retângulos prontos antes da primeira seta.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div role="list" aria-label={ariaLabel}>
          {itens.map((item, indice) => children(item, extrairId(item), indice))}
        </div>
      </SortableContext>
      <div aria-live="assertive" className="sr-only" style={estiloRegiaoAcessivel}>
        {anuncio}
      </div>
    </DndContext>
  );
}

/**
 * Item ordenável. Aplica transform/transition ao style (o que faltava em
 * DommusDraggable) e expõe a alça de arraste via render-prop `children`.
 *
 * Props:
 * - id: id estável do item.
 * - children: função (alca) => ReactNode. `alca` traz { attributes, listeners,
 *   ref } para aplicar ao elemento de alça (handle), permitindo arraste só pela alça.
 */
export function DommusSortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const alca = {
    ref: setActivatorNodeRef,
    ...attributes,
    ...listeners,
  };

  return (
    <div ref={setNodeRef} style={style} role="listitem">
      {children(alca)}
    </div>
  );
}

const estiloRegiaoAcessivel = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};
